import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { centsToMajorString, majorStringToCents } from '@/shared/lib/money';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';

interface CashShift {
  id: string;
}

interface SurpriseCountDialogProps {
  shift: CashShift;
  submitting: boolean;
  on_close: () => void;
  on_submit: (input: { declared_cash_cents: string; reason: string }) => void | Promise<void>;
}

export function SurpriseCountDialog({
  shift,
  submitting,
  on_close,
  on_submit,
}: SurpriseCountDialogProps): JSX.Element {
  const { t } = useTranslation();
  const [amount_major, setAmountMajor] = useState('0.00');
  const [reason, setReason] = useState('');
  void shift;

  const declared_cash_cents = useMemo(() => {
    try {
      return majorStringToCents(amount_major || '0').toString();
    } catch {
      return '';
    }
  }, [amount_major]);

  const can_submit = declared_cash_cents.length > 0 && !submitting;

  const onClickSubmit = async (): Promise<void> => {
    if (!can_submit) return;
    await on_submit({ declared_cash_cents, reason });
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur"
    >
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t('cash_shifts.surprise_dialog_title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">{t('cash_shifts.surprise_hint')}</p>
          <div className="space-y-1">
            <Label htmlFor="surp-amount">{t('cash_shifts.declared_cash')}</Label>
            <Input
              id="surp-amount"
              inputMode="decimal"
              className="font-mono"
              value={amount_major}
              onChange={(e) => setAmountMajor(e.target.value)}
              disabled={submitting}
            />
            <p className="text-xs text-muted-foreground">
              {t('cash_shifts.amount_hint', {
                value: centsToMajorString(declared_cash_cents || '0'),
              })}
            </p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="surp-reason">{t('cash_shifts.reason_optional')}</Label>
            <Input
              id="surp-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={submitting}
            />
          </div>
        </CardContent>
        <CardFooter className="justify-end gap-2">
          <Button type="button" variant="ghost" onClick={on_close} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            onClick={() => {
              void onClickSubmit();
            }}
            disabled={!can_submit}
          >
            {submitting ? t('common.loading') : t('cash_shifts.record')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
